import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { API_BASE } from '../../api/starviewApi';
import './RestaurantPortal.css';

const GATEWAY_BASE = `${API_BASE}/api/api/v1`;
const POLL_INTERVAL_MS = 6000;

// The only forward transitions a restaurant is allowed to make - matches
// RESTAURANT_ALLOWED_STATUSES in aserver/api's staff.ts controller exactly.
// Delivery-side transitions (out_for_delivery/delivered) only ever happen
// through the driver app, never here.
const NEXT_STATUS = {
  pending: { status: 'confirmed', label: 'Confirm Order' },
  confirmed: { status: 'preparing', label: 'Start Preparing' },
  preparing: { status: 'ready', label: 'Mark Ready for Pickup' },
};

// The backend accepts 'cancelled' from any of these (RESTAURANT_ALLOWED_STATUSES
// has no per-status transition gate at all server-side) - previously the
// client only ever showed a cancel/decline option while an order was still
// 'pending', so a restaurant that had already confirmed an order (or started
// preparing it) had no way to cancel from a real, common situation - out of
// an ingredient, kitchen closing early - even though the backend already
// allowed it. Once a driver could be en route ('ready' onward) cancelling
// here no longer makes sense, so the cutoff stays there.
const CANCELLABLE_STATUSES = new Set(['pending', 'confirmed', 'preparing']);

// A short, synthesized two-tone chime (Web Audio API, no asset file needed)
// so kitchen staff who aren't staring at the screen still notice a new
// order - the previous version relied entirely on the dashboard silently
// re-rendering every 6s with no audible or visual cue at all.
function playNewOrderChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [880, 1108].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.001, ctx.currentTime + i * 0.18);
      gain.gain.exponentialRampToValueAtTime(0.2, ctx.currentTime + i * 0.18 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.35);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + i * 0.18);
      osc.stop(ctx.currentTime + i * 0.18 + 0.36);
    });
    setTimeout(() => ctx.close(), 800);
  } catch (e) {
    // Best-effort only - never worth surfacing an error over a chime.
  }
}

const STATUS_LABELS = {
  pending: 'New Order',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready for Pickup',
  out_for_delivery: 'Out for Delivery',
  arrived: 'Driver Arrived',
  delivered: 'Delivered',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function formatMoney(amount, currency) {
  if (typeof amount !== 'number') return '';
  return `${currency || 'USD'} ${amount.toFixed(2)}`;
}

function LoginForm() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(username, password);
    } catch (err) {
      setError(err.message || 'Failed to sign in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rp-root rp-center">
      <form className="rp-login-card" onSubmit={handleSubmit}>
        <div className="rp-eyebrow">ORDERUP FOR RESTAURANTS</div>
        <h1 className="rp-login-title">Partner Portal</h1>
        <p className="rp-login-subtitle">Sign in with the account StarView created for your restaurant.</p>
        <input
          className="rp-input"
          type="text"
          placeholder="Email"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoComplete="username"
        />
        <input
          className="rp-input"
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
        />
        {error && <div className="rp-error">{error}</div>}
        <button className="rp-button" type="submit" disabled={loading}>
          {loading ? 'Signing in…' : 'Sign In'}
        </button>
        <p className="rp-apply-link">
          New restaurant? <Link to="/partner/restaurant/apply">Apply to become a partner</Link>
        </p>
      </form>
    </div>
  );
}

function OrderCard({ order, onAdvance, onCancel, busyOrderId }) {
  const next = NEXT_STATUS[order.status];
  const isBusy = busyOrderId === (order._id || order.id);
  return (
    <div className="rp-order-card">
      <div className="rp-order-header">
        <div>
          <div className="rp-order-number">Order #{order.orderNumber}</div>
          <div className="rp-order-time">{new Date(order.orderPlacedAt || order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
        </div>
        <div className={`rp-status-pill rp-status-${order.status}`}>{STATUS_LABELS[order.status] || order.status}</div>
      </div>
      <div className="rp-order-items">
        {(order.items || []).map((item, idx) => (
          <div className="rp-order-item" key={idx}>
            <span className="rp-item-qty">{item.quantity}×</span>
            <span className="rp-item-name">{item.menuItemName}</span>
            <span className="rp-item-price">{formatMoney(item.totalPrice, order.pricing?.currency)}</span>
          </div>
        ))}
      </div>
      {order.specialInstructions ? <div className="rp-instructions">"{order.specialInstructions}"</div> : null}
      <div className="rp-order-footer">
        <div className="rp-order-total">{formatMoney(order.pricing?.total, order.pricing?.currency)}</div>
        <div className="rp-order-actions">
          {CANCELLABLE_STATUSES.has(order.status) && (
            <button className="rp-action-cancel" onClick={() => onCancel(order)} disabled={isBusy}>
              {order.status === 'pending' ? 'Decline' : 'Cancel'}
            </button>
          )}
          {next && (
            <button className="rp-action-advance" onClick={() => onAdvance(order, next.status)} disabled={isBusy}>
              {isBusy ? 'Updating…' : next.label}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const { authFetch, user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyOrderId, setBusyOrderId] = useState(null);
  const [isOpen, setIsOpen] = useState(null);
  const [togglingOpen, setTogglingOpen] = useState(false);
  // Tracks which pending order IDs we've already chimed for, so the alert
  // fires once per new order rather than on every 6s poll while it's still
  // sitting unconfirmed.
  const knownPendingIdsRef = useRef(new Set());
  const firstLoadRef = useRef(true);

  const loadOrders = useCallback(async () => {
    try {
      const res = await authFetch(`${GATEWAY_BASE}/restaurant-portal/orders`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load orders');
      const fetched = data.orders || [];
      const pendingIds = fetched.filter((o) => o.status === 'pending').map((o) => o._id || o.id);
      if (!firstLoadRef.current) {
        const hasNewPending = pendingIds.some((id) => !knownPendingIdsRef.current.has(id));
        if (hasNewPending) playNewOrderChime();
      }
      knownPendingIdsRef.current = new Set(pendingIds);
      firstLoadRef.current = false;
      setOrders(fetched);
      if (data.restaurant && typeof data.restaurant.isOpen === 'boolean') setIsOpen(data.restaurant.isOpen);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  // Previously there was no way at all for a restaurant to pause taking new
  // orders themselves (a busy rush, closing early, a kitchen problem) short
  // of contacting StarView directly - the underlying isOpen field and its
  // full update pipeline already existed, just with no staff-facing route.
  const handleToggleOpen = async () => {
    const next = !isOpen;
    setIsOpen(next);
    setTogglingOpen(true);
    try {
      const res = await authFetch(`${GATEWAY_BASE}/restaurant-portal/status`, {
        method: 'PATCH',
        body: JSON.stringify({ isOpen: next }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update restaurant status');
    } catch (err) {
      setIsOpen(!next);
      setError(err.message);
    } finally {
      setTogglingOpen(false);
    }
  };

  useEffect(() => {
    loadOrders();
    const id = setInterval(loadOrders, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [loadOrders]);

  // Real-time push (OrderUp Worldwide, Phase 6): join this restaurant's
  // socket room on the gateway so new orders ring the chime the moment
  // they're placed, and status changes appear without waiting out the poll
  // interval. The poll above keeps running as the fallback - a dropped
  // socket only means the old cadence, never missed orders. The chime
  // dedupe in loadOrders (knownPendingIdsRef) already guarantees one ring
  // per order no matter which path noticed it first.
  const restaurantIdRef = useRef(null);
  useEffect(() => {
    const found = orders.find((o) => o.restaurantId);
    if (found) restaurantIdRef.current = String(found.restaurantId);
  }, [orders]);

  useEffect(() => {
    let socket;
    let joined = null;
    let retryTimer;
    const tryJoin = () => {
      const restaurantId = restaurantIdRef.current;
      if (!socket || !restaurantId || joined === restaurantId) {
        retryTimer = setTimeout(tryJoin, 3000);
        return;
      }
      socket.emit('orderup:joinRestaurant', restaurantId);
      joined = restaurantId;
    };
    (async () => {
      try {
        const { io } = await import('socket.io-client');
        socket = io(API_BASE, { transports: ['websocket'], reconnectionAttempts: 10 });
        socket.on('connect', () => { joined = null; tryJoin(); });
        socket.on('orderup:newOrder', loadOrders);
        socket.on('orderup:orderUpdate', loadOrders);
        tryJoin();
      } catch (err) {
        console.warn('[RestaurantPortal] Socket unavailable, polling only:', err.message);
      }
    })();
    return () => {
      clearTimeout(retryTimer);
      try { socket?.disconnect(); } catch { /* noop */ }
    };
  }, [loadOrders]);

  const handleAdvance = async (order, status) => {
    const orderId = order._id || order.id;
    setBusyOrderId(orderId);
    try {
      const res = await authFetch(`${GATEWAY_BASE}/restaurant-portal/orders/${orderId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update order');
      await loadOrders();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyOrderId(null);
    }
  };

  const handleCancel = (order) => handleAdvance(order, 'cancelled');

  // Newest active orders first, delivered/cancelled orders pushed to the back.
  const activeOrders = orders.filter((o) => !['delivered', 'completed', 'cancelled'].includes(o.status));
  const pastOrders = orders.filter((o) => ['delivered', 'completed', 'cancelled'].includes(o.status));

  return (
    <div className="rp-root">
      <div className="rp-header">
        <div>
          <div className="rp-eyebrow">ORDERUP FOR RESTAURANTS</div>
          <h1 className="rp-dashboard-title">Order Queue</h1>
        </div>
        <div className="rp-header-right">
          {isOpen !== null && (
            <button
              className={`rp-open-toggle ${isOpen ? 'rp-open-toggle-open' : 'rp-open-toggle-closed'}`}
              onClick={handleToggleOpen}
              disabled={togglingOpen}
            >
              {isOpen ? 'Open — Accepting Orders' : 'Closed — Paused'}
            </button>
          )}
          <span className="rp-username">{user?.username}</span>
          <button className="rp-logout" onClick={logout}>Sign Out</button>
        </div>
      </div>

      {error && <div className="rp-error rp-error-banner">{error}</div>}

      {loading ? (
        <div className="rp-empty">Loading orders…</div>
      ) : activeOrders.length === 0 ? (
        <div className="rp-empty">No active orders right now. New orders will appear here automatically.</div>
      ) : (
        <div className="rp-order-grid">
          {activeOrders.map((order) => (
            <OrderCard key={order._id || order.id} order={order} onAdvance={handleAdvance} onCancel={handleCancel} busyOrderId={busyOrderId} />
          ))}
        </div>
      )}

      {pastOrders.length > 0 && (
        <>
          <h2 className="rp-section-title">Recent History</h2>
          <div className="rp-order-grid rp-order-grid-compact">
            {pastOrders.slice(0, 10).map((order) => (
              <OrderCard key={order._id || order.id} order={order} onAdvance={handleAdvance} onCancel={handleCancel} busyOrderId={busyOrderId} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function RestaurantPortal() {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return <div className="rp-root rp-center"><div className="rp-empty">Loading…</div></div>;
  }
  if (!isAuthenticated) {
    return <LoginForm />;
  }
  if (user && user.role !== 'restaurant_owner') {
    return (
      <div className="rp-root rp-center">
        <div className="rp-login-card">
          <h1 className="rp-login-title">Not a Partner Account</h1>
          <p className="rp-login-subtitle">This account isn't set up as a restaurant partner. Contact StarView if you believe this is a mistake.</p>
        </div>
      </div>
    );
  }
  return <Dashboard />;
}
